import { App, Spin } from "antd";
import { Form, Formik, FormikValues } from "formik";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQueries } from "@tanstack/react-query";
import * as Yup from "yup";

import { Button, Card, Input, Select } from "../../custom";
import styles from "./styles.module.scss";
import {
  getAllApplicationBatch,
  getAllModeOfStudy,
  getAllPrograms,
  registerUser,
} from "../../utils/auth_request";
import { validator } from "../../utils/validator";
import { routes } from "../../routes";
import {
  getAllProgramTypes,
  getCountries,
  getInstitutionByShortName,
  getLgaByStateId,
  getStateByCountryId,
} from "../../utils/request";
import { useState } from "react";
import { applicantTypeMap, INSTITUTION_NAME } from "../../utils/constants";
import useGetSpecializationByProgramIdQuery from "../../hooks/api/useGetSpecializationByProgramIdQuery";
import api from "../../utils/api";
import { getAllApplicationFeeByProgramId } from "../courseDetails/request";

// Bachelor's, ND and HND applicants all pick UTME/Direct Entry - only
// postgraduate program types (Masters, PhD, etc.) skip mode of entry.
const requiresModeOfEntry = (programTypeName?: string) => {
  const normalized = programTypeName?.trim().toLowerCase() ?? "";
  return (
    normalized.startsWith("b") || normalized === "nd" || normalized === "hnd"
  );
};

// A batch is open when it hasn't been force-closed (isActive) and today
// falls within its window - the late window (if any) extends the deadline
// rather than replacing it, matching how useApplicationPeriod treats it.
const isApplicationBatchOpen = (batch: getAllApplicationBatch) => {
  if (batch.isActive === false) return false;

  const now = new Date();
  const startDate = batch.startDate ? new Date(batch.startDate) : null;
  const endDate = batch.endDate ? new Date(batch.endDate) : null;
  const lateEndDate = batch.lateEndDate ? new Date(batch.lateEndDate) : null;
  const periodEndDate = lateEndDate ?? endDate;

  if (!startDate || !periodEndDate) return false;

  return now >= startDate && now <= periodEndDate;
};

const normalizeForMatch = (value?: string) =>
  value?.trim().toLowerCase().replace(/[^a-z0-9]/g, "") ?? "";

const knowAboutUsOptions = [
  { label: "LinkedIn", value: 0 },
  { label: "Instagram", value: 1 },
  { label: "Facebook", value: 2 },
  { label: "Website", value: 3 },
  { label: "Referrals", value: 4 },
  { label: "Current Student", value: 5 },
  { label: "Faculty Member", value: 6 },
  { label: "Alumni Network", value: 7 },
  { label: "Newspaper Publication", value: 8 },
].map((item) => (
  <option key={item.value} value={item.value}>
    {item.label}
  </option>
));

const Register = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { programTypeId, departmentId, id } = useParams();
  const [searchParams] = useSearchParams();
  const [countryId, setCountryId] = useState<number>(0);
  const [stateId, setStateId] = useState<number>(0);
  const programId = Number(departmentId);
  const mainProgramId = Number(id);

  const specializationQuery = useGetSpecializationByProgramIdQuery({ Id: mainProgramId });

  const registerMutation = useMutation({
    mutationKey: ["register"],
    mutationFn: registerUser,
  });

  const queries = useQueries({
    queries: [
      {
        queryKey: ["get-institution"],
        queryFn: () => getInstitutionByShortName(INSTITUTION_NAME || ""),
        retry: 1,
      },
      {
        queryKey: ["get-application-batch"],
        queryFn: getAllApplicationBatch,
        retry: 1,
      },

      {
        queryKey: ["get-mode-of-study"],
        queryFn: getAllModeOfStudy,
        retry: 1,
      },
      {
        queryKey: ["get-departments"],
        queryFn: getAllPrograms,
        retry: 1,
      },
      {
        queryKey: ["get-programsTypes"],
        queryFn: getAllProgramTypes,
        retry: 1,
      },
      {
        queryKey: ["get-countries"],
        queryFn: getCountries,
      },
      {
        queryKey: ["get-state-id", countryId],
        queryFn: () => getStateByCountryId(countryId),
        enabled: !!countryId,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: ["get-lga-id", stateId],
        queryFn: () => getLgaByStateId(stateId),
        enabled: !!stateId,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: ["get-program-details"],
        queryFn: async () =>
          (await api.get(`/Utilities/Utilities/GetAllProgramDetails`))
            ?.data as ProgramDataResponse,
        retry: 1,
      },
      {
        queryKey: ["get-application-fee", departmentId],
        queryFn: () => getAllApplicationFeeByProgramId(departmentId ?? ""),
        enabled: !!departmentId,
        retry: 1,
      },
    ],
  });

  const institutionQuery = queries[0];
  const applicationBatchDataQuery = queries[1];
  const modeOfStudyDataQuery = queries[2];
  const programDataQuery = queries[3];
  const programTypeQuery = queries[4];
  const countryQuery = queries[5];
  const stateQuery = queries[6];
  const lgaQuery = queries[7];
  const programDetailsQuery = queries[8];
  const applicationFeeQuery = queries[9];

  const institutionData = institutionQuery?.data?.data ?? ({} as School);
  const applicationBatchData = applicationBatchDataQuery?.data?.data ?? [];
  const modeOfStudyData = modeOfStudyDataQuery?.data?.data ?? [];
  const programData = programDataQuery?.data?.data ?? [];
  const programTypeData = programTypeQuery?.data?.data ?? [];
  const countryData = countryQuery?.data?.data ?? [];
  const stateData = stateQuery?.data?.data ?? [];
  const lgaData = lgaQuery?.data?.data ?? [];
  const programDetailsData = programDetailsQuery?.data?.data ?? [];
  const applicationFeeData = applicationFeeQuery?.data?.data ?? [];

  // Fee-setup rows for this program tell us which batches/modes of study are
  // actually configured for it. Whether a batch is open is controlled solely
  // by the batch's own isActive/date fields (isApplicationBatchOpen) - a
  // fee-setup row is only used to scope which open batches belong to this
  // program, never to override an otherwise-open batch as closed.
  const programFeeSetups = applicationFeeData.filter(
    (fee) => fee.activeStatus !== false && fee.programId === programId,
  );

  // Prefer fee-setup rows scoped to this exact program type when the setup
  // data actually specifies one, but don't let an unset/mismatched
  // programTypeId on the fee-setup row hide a batch that's genuinely open -
  // fall back to every program-scoped row if none match.
  const programTypeMatchedFeeSetups = programFeeSetups.filter(
    (fee) => Number(fee.programTypeId) === Number(programTypeId),
  );
  const relevantFeeSetups = programTypeMatchedFeeSetups.length
    ? programTypeMatchedFeeSetups
    : programFeeSetups;

  const findProgramType = programTypeData?.find(
    (item) => item?.id === Number(programTypeId),
  );
  const showsModeOfEntry = requiresModeOfEntry(findProgramType?.name);
  
  const findDepartment = programData?.find(
    (item) => item?.id === Number(departmentId),
  );
  const selectedProgram = programDetailsData?.find(
    (item) =>
      item?.programId === Number(departmentId) 
    && item?.programTypeId == Number(programTypeId),
  );  

  const selectedProgramName =
    selectedProgram?.programName ?? findDepartment?.name ?? "";

  // The pool of batches that are genuinely open right now, system-wide. A
  // batch being open elsewhere must never, by itself, open applications for
  // this program/mode of study - it only counts once it's actually tied to
  // one of this program's modes of study below, via fee-setup data or (when
  // no fee-setup exists yet) a name match. Default is closed.
  const openBatchPool = applicationBatchData?.filter(isApplicationBatchOpen) ?? [];

  // When a fee-setup ties a mode of study to a single open batch, the batch
  // is derived from the mode of study rather than picked independently -
  // this is what lets us auto-fill and lock the batch field below.
  const feeSetupModeOfStudyToBatchId = new Map(
    relevantFeeSetups
      .filter((fee) =>
        openBatchPool.some((batch) => batch.id === fee.applicationBatchId),
      )
      .map((fee) => [fee.modeOfStudyId, fee.applicationBatchId]),
  );

  // Many programs don't have fee-setup rows configured yet (the endpoint
  // just returns an empty list for them). When that's the case, fall back to
  // matching a batch's name against the mode of study's name - e.g. a
  // "Full-Time (2026/2027)" batch for the "Full Time" mode of study - since
  // that's the same naming convention admissions staff already use for
  // batches. The fee-setup mapping always wins when it actually has data.
  const nameMatchedModeOfStudyToBatchId = new Map<number, number>();
  if (!relevantFeeSetups.length) {
    modeOfStudyData
      ?.filter((mode) => mode.activeStatus === true)
      .forEach((mode) => {
        const normalizedModeName = normalizeForMatch(mode.name);
        if (!normalizedModeName) return;

        const matchedBatch = openBatchPool.find((batch) =>
          normalizeForMatch(batch.batchName).includes(normalizedModeName),
        );
        if (matchedBatch) {
          nameMatchedModeOfStudyToBatchId.set(mode.id, matchedBatch.id);
        }
      });
  }

  const modeOfStudyToBatchId = relevantFeeSetups.length
    ? feeSetupModeOfStudyToBatchId
    : nameMatchedModeOfStudyToBatchId;

  // A mode of study is only offered for this program right now if it
  // actually resolves to an open batch above - via fee-setup data, or (when
  // no fee-setup exists) a name match. There is no fallback that shows a
  // mode of study as available just because some unrelated batch is open.
  const activeModeOfStudy = modeOfStudyData?.filter(
    (mode) => mode.activeStatus === true && modeOfStudyToBatchId.has(mode.id),
  );

  // Only the batches genuinely tied to one of this program's open modes of
  // study are selectable - never every open batch in the system.
  const matchedBatchIds = new Set(modeOfStudyToBatchId.values());
  const openApplicationBatches = openBatchPool.filter((batch) =>
    matchedBatchIds.has(batch.id),
  );

  const applicationBatchOptions = openApplicationBatches?.map((item) => (
    <option key={item?.id} value={item.id}>
      {item.batchName}
    </option>
  ));

  // The home page lets a student pick a mode of study before "Apply", passed
  // through as ?modeOfStudyId=. Only honour it if it's still a genuinely
  // valid, open choice for this program - otherwise leave both fields blank
  // for manual selection, same as landing here directly.
  const modeOfStudyIdParam = searchParams.get("modeOfStudyId");
  const requestedModeOfStudy = modeOfStudyData?.find(
    (mode) => `${mode.id}` === modeOfStudyIdParam,
  );
  const initialModeOfStudyId =
    modeOfStudyIdParam &&
    activeModeOfStudy?.some((mode) => `${mode.id}` === modeOfStudyIdParam)
      ? modeOfStudyIdParam
      : "";
  const initialApplicationBatchId = initialModeOfStudyId
    ? modeOfStudyToBatchId.get(Number(initialModeOfStudyId))
    : undefined;

  const isInitialDataLoading =
    applicationBatchDataQuery.isLoading || applicationFeeQuery.isLoading;

  // A student can arrive here already targeting one mode of study (e.g. the
  // "Apply - Full Time" button on the home page passes ?modeOfStudyId=). If
  // that specific mode has no open batch, show the closed message right away
  // even though another mode of study for the same program is still open -
  // otherwise the requested mode just silently disappears from the dropdown.
  const isRequestedModeOfStudyClosed =
    !!modeOfStudyIdParam &&
    applicationBatchDataQuery.isSuccess &&
    !activeModeOfStudy?.some((mode) => `${mode.id}` === modeOfStudyIdParam);

  const isApplicationClosed =
    (applicationBatchDataQuery.isSuccess && !openApplicationBatches?.length) ||
    isRequestedModeOfStudyClosed;

  const modeOfEntryData = [
    { label: "UTME", value: 1 },
    { label: "Direct Entry", value: 2 },
    // { label: "O Level", value: 3 },
    // { label: "ND", value: 4 },


    // { label: "Transfer", value: 3 },
  ];

  const modeOfEntryOptions = modeOfEntryData?.map((item) => (
    <option key={item?.value} value={item?.value}>
      {item?.label}
    </option>
  ));

  const modeOfStudyOptions = activeModeOfStudy?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const countryOptions = countryData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.countryName}
    </option>
  ));

  const stateOptions = stateData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.stateName}
    </option>
  ));

  const lgaOptions = lgaData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.lgaName}
    </option>
  ));

  const specializationOptions = specializationQuery.specializations.map((item) => (
    <option key={item.id} value={item.id}>
      {item.name}
    </option>
  ));

  const validationSchema = Yup.object().shape({
    email: validator.email,
    firstName: validator.firstName,
    lastName: validator.lastName,
    phone: validator.phone,
    password: validator.password,
    confirmPassword: validator.confirmPassword,
    department: validator.department,
    selectModeOfStudy: validator.selectModeOfStudy,
    selectApplication: validator.selectApplication,
    programType: validator.selectProgramType,
    knowAboutUs: validator.knowAboutUs,
  });

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const getApplicantType = (countryId: number, stateId: number) => {
    if (countryId === institutionData?.countryId) {
      if (stateId === institutionData?.stateId) {
        return applicantTypeMap.Indigene;
      } else return applicantTypeMap.Regular;
    } else return applicantTypeMap.International;
  };

  const handleRegister = async (values: FormikValues) => {
    const applicantType = getApplicantType(countryId, stateId);
    const payload: RegisterPayload = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phone,
      password: values.password,
      confirmPassword: values.confirmPassword,
      programId,
      modeOfStudyId: Number(values.selectModeOfStudy),
      applicationBatchId: Number(values.selectApplication),
      programTypeId: Number(programTypeId),
      concentrationId: Number(values.selectSpecialization) || 0,
      applicantType: applicantType,
      countryId: countryId,
      stateId: stateId,
      lgaId: Number(values.lga),
      knowAboutUs: Number(values.knowAboutUs),
    };

    if (showsModeOfEntry) {
      payload.modeOfEntry = Number(values.selectModeOfEntry);
    }

    try {
      await registerMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });

          navigate(`${routes.auth.verifyEmail}?email=${values.email}`);
          // resetForm();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  if (isInitialDataLoading) {
    return (
      <section className={styles.container}>
        <div>
          <Card>
            <div className={styles.cardChild}>
              <Spin size="large" />
            </div>
          </Card>
        </div>
      </section>
    );
  }

  if (isApplicationClosed) {
    return (
      <section className={styles.container}>
        <div>
          <Card>
            <div className={styles.cardChild}>
              <div>
                <h3 className={styles.closedMessage}>
                  Applications for {selectedProgramName || "this program"}
                  {isRequestedModeOfStudyClosed && requestedModeOfStudy?.name
                    ? ` (${requestedModeOfStudy.name})`
                    : ""}{" "}
                  are currently closed
                </h3>
                <p>
                  The application window for this mode of study has ended.
                  Please check back for the next intake or contact
                  admissions for further assistance.
                </p>
              </div>

              <div className={styles.buttonGroup}>
                <Button
                  type="button"
                  text="BACK"
                  onClick={handleBackNavigation}
                />
              </div>
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      <div>
        <Card>
          <div className={styles.cardChild}>
            <div>
              <h3>Welcome to our portal</h3>
              <p>To Register please enter your information below</p>
            </div>

            <div>
              <div>
                <Formik
                  initialValues={{
                    email: "",
                    firstName: "",
                    phone: "",
                    lastName: "",
                    password: "",
                    confirmPassword: "",
                    selectApplication: initialApplicationBatchId
                      ? `${initialApplicationBatchId}`
                      : "",
                    selectModeOfStudy: initialModeOfStudyId,
                    selectSpecialization: "",
                    selectedProgram: selectedProgramName,
                    department: selectedProgram?.program ?? "",
                    programType: selectedProgram?.programTypeName ?? "",
                    country: "",
                    state: "",
                    lga: "",
                    knowAboutUs: "",
                  }}
                  onSubmit={(values, resetForm) => {
                    handleRegister(values);
                  }}
                  enableReinitialize={true}
                  validationSchema={validationSchema}
                >
                  {({ handleChange, setFieldValue }) => {
                    return (
                      <Form>
                        <div className={styles.formStyle}>

                          <Input name="firstName" label="First Name:" />
                          <Input name="lastName" label="Last Name:" />
                          <Input name="email" label="Email:" />
                          <Input name="phone" label="Phone Number:" />
                          <Select
                            label="Select Country:"
                            name="country"
                            placeholder="Select Country"
                            options={countryOptions}
                            onChange={(e) => {
                              handleChange(e);
                              setCountryId(parseInt(e.target.value));
                              setFieldValue("country", e.target.value);
                            }}
                          />
                          <Select
                            label="Select State:"
                            name="state"
                            placeholder="Select State"
                            options={stateOptions}
                            onChange={(e) => {
                              handleChange(e);
                              setStateId(parseInt(e.target.value));
                              setFieldValue("state", e.target.value);
                            }}
                            disabled={!countryId}
                          />
                          <Select
                            label="Select LGA:"
                            name="lga"
                            placeholder="Select Local Government Area"
                            options={lgaOptions}
                            onChange={handleChange}
                            disabled={!stateId}
                          />
                          <Input
                            type="password"
                            name="password"
                            label="Password:"
                          />
                          <Input
                            name="confirmPassword"
                            label="Confirm Password:"
                            type="password"
                          />

                          <Select
                            label="Select Mode of Study:"
                            name="selectModeOfStudy"
                            placeholder="Select Mode Of Study"
                            options={modeOfStudyOptions}
                            onChange={(e) => {
                              handleChange(e);
                              const matchedBatchId = modeOfStudyToBatchId.get(
                                Number(e.target.value),
                              );
                              setFieldValue(
                                "selectApplication",
                                matchedBatchId ? `${matchedBatchId}` : "",
                              );
                            }}
                          />
                          {showsModeOfEntry && (
                            <Select
                              label="Select Mode of Entry:"
                              name="selectModeOfEntry"
                              placeholder="Select Mode Of Entry"
                              options={modeOfEntryOptions}
                              onChange={handleChange}
                            />
                          )}

                           <Select
                            name="selectApplication"
                            placeholder="Select Application Batch"
                            label="Select Application Batch:"
                            options={applicationBatchOptions}
                            onChange={handleChange}
                            disabled={modeOfStudyToBatchId.size > 0}
                          />
                            <Input
                            name="department"
                            label="Department"
                            readOnly
                            disabled
                          />
                            <Input
                            name="programType"
                            label="Program Type"
                            readOnly
                            disabled
                          />
                         
                          {specializationQuery.hasSpecializations && (
                            <Select
                              label="Select Specialization/Concentration (Optional):"
                              name="selectSpecialization"
                              placeholder="Select Specialization"
                              options={specializationOptions}
                              onChange={handleChange}
                            />
                          )}
                          <Select
                            label="How did you hear about us:"
                            name="knowAboutUs"
                            placeholder="Select Option"
                            options={knowAboutUsOptions}
                            onChange={handleChange}
                          />
                        </div>

                        <div>
                          <div className={styles.buttonGroup}>
                            <Button
                              type="submit"
                              text="REGISTER"
                              disabled={registerMutation.isPending}
                              isLoading={registerMutation?.isPending}
                            />

                            <Button
                              type="button"
                              text="BACK"
                              onClick={handleBackNavigation}
                            />
                          </div>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <Link to={routes.auth.login}>
              <p>Already have an account? Login</p>
            </Link>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default Register;
